import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { DateRangePicker } from "rsuite";
import Select from "react-select";
import moment from "moment";
import qs from "qs";
import Breadcrumbs from "../../common/breadcrumbs";
import Table from "../../common/table";
import Icon from "../../icons/index";
import { useStore } from "../../../zustand/store";
import CS from "../../../services/commonService";
import {
	useGetUnclaimedPayout,
	useInitiateUnclaimedTransfer,
} from "../../../queryHooks/usePayouts";
import { useGetAllRestaurants } from "../../../queryHooks";

import { URL } from "../../../constants/URL";
import { modules } from "../../../constants/Modules";
import TableLoader from "../../../loaders/tableLoader";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";

const { afterToday } = DateRangePicker;

const UnClaimedGiftsPayout = () => {
	const navigate = useNavigate();
	const setLoading = useStore((state) => state.setLoading);

	const [tableData, setTableData] = useState([]);
	const lastPageData = useStore((state) => state.lastPageData);
	const setLastPageData = useStore((state) => state.setLastPageData);
	// todo: add pagination or remove this pagination configuration
	const [itemsPerPage, setItemsPerPage] = useState(
		lastPageData?.[modules.UNCLAIMED_GIFTS]?.items_per_page || 10
	);

	const [restaurants, setRestaurants] = useState([]);
	const [startDate, setStartDate] = useState(
		lastPageData?.[modules.UNCLAIMED_GIFTS]?.filter_date?.created_at?.[0]
	);
	const [endDate, setEndDate] = useState(
		lastPageData?.[modules.UNCLAIMED_GIFTS]?.filter_date?.created_at?.[1]
	);
	const [selectedRestaurant, setSelectedRestaurant] = useState(
		lastPageData?.selectedRestaurant
	);
	const [isTransferEligible, setIsTransferEligible] = useState(false);

	const [payload, setPayload] = useState({
		pagination: false,
	});

	const { data: dataGetAllRestaurants } = useGetAllRestaurants(
		qs.stringify({
			pagination: false,
			filter: {
				["admin_approval_status"]: "APPROVED",
			},
		})
	);

	useEffect(() => {
		if (dataGetAllRestaurants && dataGetAllRestaurants.data?.records) {
			let _restaurants = [];
			dataGetAllRestaurants.data.records.forEach((element) => {
				_restaurants.push({
					label: element.name,
					value: element.id,
				});
			});
			setRestaurants(_restaurants);
		}
	}, [dataGetAllRestaurants]);

	const {
		data: unclaimedPayoutData,
		error: unclaimedPayoutError,
		isLoading,
	} = useGetUnclaimedPayout({
		restaurantId: selectedRestaurant?.value,
		queryParams: qs.stringify(payload),
	});

	useEffect(() => {
		if (unclaimedPayoutData?.status === "success") {
			let respData = unclaimedPayoutData?.data;
			respData.page = 1;
			respData.total_items = unclaimedPayoutData?.data?.records?.length;
			respData.items_per_page = unclaimedPayoutData?.data?.records?.length;
			setTableData(respData);
		}
	}, [unclaimedPayoutData]);

	useEffect(() => {
		if (unclaimedPayoutError) {
			CS.showErrorMessage(
				unclaimedPayoutError?.response?.data?.message ||
					unclaimedPayoutError?.data?.message ||
					unclaimedPayoutError?.message
			);
		}
	}, [unclaimedPayoutError]);

	const columns = [
		{
			label: "Gift Id",
			type: "string",
			maxWidth: true,
			path: "gift_id",
			content: (item) => (
				<span
					className={item?.order_info?.is_transfer_eligible ? "active" : "blur"}
				>
					{item?.gift_id}
				</span>
			),
		},
		{
			label: "Date",
			type: "date",
			path: "created_at",
			alias: "created_at",
			sort: true,
			// filterDate: true,
			content: (item) => (
				<span
					className={item?.order_info?.is_transfer_eligible ? "active" : "blur"}
				>
					{item?.created_at ? getCurrentTimezoneDate(item.created_at) : null}
				</span>
			),
		},
		{
			label: "User",
			type: "string",
			path: "orders.user_id",
			// aliasFilter: "orders.user_id",
			alias: "user.first_name",
			// filterOptions: users,
			content: (item) => (
				<Link
					to={{
						pathname: `${URL.onboarderedUsers}/${item.user?.user_id}`,
						state: { key: "giftOrders" },
					}}
					onClick={() => {
						setLastPageData({
							[modules.UNCLAIMED_GIFTS]: payload,
							selectedRestaurant,
						});
					}}
					className={item?.order_info?.is_transfer_eligible ? "" : "blur"}
				>
					<div className="d-flex align-items-center pr-3">
						<div className="d-flex flex-column flex-wrap">
							<span className="pl-2">
								{item.user?.first_name} {item.user?.last_name}
							</span>
						</div>
					</div>
				</Link>
			),
		},
		{
			label: "Gross Amount",
			type: "string",
			// sort: true,
			path: "gross_amount",
			alias: "gross_amount",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.gross_amount
						? "$" + item?.order_info?.gross_amount
						: "--"}
				</div>
			),
		},

		{
			label: "Discount",
			type: "string",
			// sort: true,
			path: "discount",
			alias: "discount",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.discount ? "$" + item?.order_info?.discount : "--"}
				</div>
			),
		},
		{
			label: "Estimated Tax",
			type: "string",
			// sort: true,
			path: "tax",
			alias: "tax",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.tax ? "$" + item?.order_info?.tax : "--"}
				</div>
			),
		},

		{
			label: "Tip",
			type: "string",
			path: "tip",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.tip ? "$" + item?.order_info?.tip : "--"}
				</div>
			),
		},
		{
			label: "Net Amount",
			type: "string",
			path: "net_amount",
			alias: "net_amount",
			// sort: true,
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.net_amount
						? "$" + item?.order_info?.net_amount
						: "--"}
				</div>
			),
		},
		{
			label: "Platform Fee",
			type: "string",
			// sort: true,
			path: "platform_fee",
			alias: "platform_fee",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.platform_fee
						? "$" + item?.order_info?.platform_fee
						: "--"}
				</div>
			),
		},

		{
			label: "Merchant Fee",
			type: "string",
			// sort: true,
			path: "merchant_fee",
			alias: "merchant_fee",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.merchant_fee
						? "$" + item?.order_info?.merchant_fee
						: "--"}
				</div>
			),
		},
		{
			label: "Net Payable Restaurant",
			type: "string",
			// sort: true,
			path: "net_restaurant_amount",
			alias: "net_restaurant_amount",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.net_restaurant_amount
						? "$" + item?.order_info?.net_restaurant_amount
						: "--"}
				</div>
			),
		},
		{
			label: "Net Payable OPA",
			type: "string",
			// sort: true,
			path: "net_platform_amount",
			alias: "net_platform_amount",
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.order_info?.net_platform_amount
						? "$" + item?.order_info?.net_platform_amount
						: "--"}
				</div>
			),
		},
		{
			label: "Payment Status",
			type: "string",
			path: "payment_status",
			// aliasFilter: "payment_status.value",
			// filterOptions: paymentOptions,
			content: (item) => (
				<div
					className={
						item?.order_info?.is_transfer_eligible
							? "d-flex justify-content-center active"
							: "d-flex justify-content-center blur"
					}
				>
					{item?.payment_status ? item?.payment_status?.value : "--"}
				</div>
			),
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="show"
						data-tip
						data-for="show"
						onClick={() => {
							setLastPageData({
								[modules.UNCLAIMED_GIFTS]: payload,
								selectedRestaurant,
							});
							navigate(`${URL.transactions}/${item?.id}`);
						}}
					/>
					<ReactTooltip id="show" place="top" effect="solid">
						Show
					</ReactTooltip>
				</>
			),
		},
	];

	useEffect(() => {
		if (!!startDate && !!endDate) {
			setPayload({
				...payload,
				filter_date: { created_at: [startDate, endDate] },
			});
		}
	}, [startDate, endDate]);

	const handleChangeDate = (dates) => {
		if (dates) {
			setStartDate(CS.convertDateToUTC(dates[0]));
			setEndDate(CS.convertDateToUTC(dates[1], 23, 59, 59));
		} else {
			setStartDate(null);
			setEndDate(null);
		}
	};

	const {
		mutateAsync: createTransfer,
		data: unclaimedTransferData,
		error: unclaimedTransferError,
		isLoading: unclaimedTransferLoading,
	} = useInitiateUnclaimedTransfer();

	useEffect(() => {
		setLoading(unclaimedTransferLoading);
	}, [unclaimedTransferLoading]);

	useEffect(() => {
		if (unclaimedTransferData?.status === "success") {
			CS.showSuccessMessage(
				unclaimedTransferData?.data?.message || unclaimedTransferData?.message
			);
			navigate(URL.payoutsHistory);
		}
	}, [unclaimedTransferData]);

	useEffect(() => {
		if (unclaimedTransferError) {
			CS.showErrorMessage(
				unclaimedTransferError?.response?.data?.message ||
					unclaimedTransferError?.data?.message ||
					unclaimedTransferError?.message
			);
		}
	}, [unclaimedTransferError]);

	const handleTransfer = () => {
		createTransfer({
			restaurantId: selectedRestaurant?.value,
			body: {
				date_filter: {
					start_date: startDate,
					end_date: endDate,
					timeZone: CS.getTimezone(),
				},
			},
		});
	};

	return (
		<React.Fragment>
			<Breadcrumbs
				title="Payouts Management"
				data={[
					{ label: "Payout History", path: URL.payoutsHistory },
					{ label: "Unclaimed Gifts - Transfer Funds" },
				]}
			/>
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div id="accordion2" className="collapse-setting">
								<div className="card mb-5 bg-transparent">
									<div
										id="collapseTwo"
										className="collapse show"
										aria-labelledby="headingTwo"
										data-parent="#accordion2"
									>
										<div className="card-strip h-100">
											{/* <div className="user-listing-filterOptions mb-2 mt-3 d-block"> */}
											<div id="accordion" className="collapse-setting">
												<div className="card mb-5 bg-transparent">
													<div
														id="collapseOne"
														className="collapse show"
														aria-labelledby="headingOne"
														data-parent="#accordion"
													>
														<div className="card-strip h-100">
															<div className="modal-header p-0 pb-3 mb-4 w-100">
																<h5 className="modal-title">
																	Unclaimed Gifts - Transfer Funds
																</h5>
															</div>
															<div className="row mb-2">
																<div className="form-group col-md-6 d-flex">
																	<label
																		htmlFor="dates"
																		className="align-self-center"
																	>
																		Select Date Range
																		<sup className="text-danger">*</sup>
																	</label>
																	<div className="col-md-8">
																		<div className="w-100">
																			<DateRangePicker
																				format="MM/dd/yyyy"
																				// value={
																				// 	startDate && endDate
																				// 		? [
																				// 				new Date(startDate),
																				// 				new Date(endDate),
																				// 		  ]
																				// 		: null
																				// }
																				style={{ width: "100%" }}
																				placeholder="Select Date Range"
																				disabledDate={afterToday()}
																				onChange={handleChangeDate}
																				onClean={handleChangeDate}
																			/>
																		</div>
																	</div>
																</div>

																<div className="form-group col-md-6 d-flex">
																	{/* <div className="row"> */}
																	<label
																		htmlFor="restaurant"
																		className="align-self-center"
																	>
																		Select Restaurant
																		<sup className="text-danger">*</sup>
																	</label>
																	<div className="col-md-8">
																		<Select
																			value={selectedRestaurant}
																			name="restaurant"
																			placeholder="Select restaurant"
																			onChange={(val) => {
																				setSelectedRestaurant(val);
																			}}
																			options={restaurants}
																		/>
																	</div>
																	{/* </div> */}
																</div>
															</div>
														</div>
														{/* </div> */}
													</div>
												</div>
											</div>

											{unclaimedPayoutData?.status === "success" &&
											!!startDate &&
											!!endDate ? (
												<div id="accordion2" className="collapse-setting">
													<div className="card mb-5 bg-transparent">
														<div
															id="collapseTwo"
															className="collapse show"
															aria-labelledby="headingTwo"
															data-parent="#accordion2"
														>
															<div className="card-strip h-100">
																<div className="row justify-content-between">
																	{/* <div className="d-flex justify-content-between"> */}
																	<div className="form-group col-md-8">
																		<p className="align-self-center m-0">
																			Total sum of unclaimed gifts is{" "}
																			<strong>
																				$
																				{unclaimedPayoutData?.data
																					?.total_restaurant_amount
																					? unclaimedPayoutData?.data
																							?.total_restaurant_amount
																					: 0}
																			</strong>
																		</p>
																		<p className="align-self-center">
																			Total amount eligible for transfer is{" "}
																			<strong>
																				$
																				{unclaimedPayoutData?.data
																					?.net_payable_to_restaurant
																					? unclaimedPayoutData?.data
																							?.net_payable_to_restaurant
																					: 0}
																			</strong>
																		</p>
																		<p className="align-self-center">
																			Total amount Ineligible for transfer is{" "}
																			<strong>
																				$
																				{unclaimedPayoutData?.data
																					?.total_amount_ineligible_for_transfer
																					? unclaimedPayoutData?.data
																							?.total_amount_ineligible_for_transfer
																					: 0}
																			</strong>
																		</p>
																	</div>
																	{/* </div> */}
																	<div className="col-md-4">
																		<button
																			className="btn btn-primary float-right"
																			disabled={
																				unclaimedPayoutData?.data
																					?.net_payable_to_restaurant
																					? false
																					: true
																			}
																			onClick={handleTransfer}
																		>
																			Initiate Transfer
																		</button>
																	</div>
																</div>
																<div className="mb-2 mt-2">
																	<h5 className="modal-title">
																		Unclaimed Gifts selected in this Transfer
																	</h5>
																</div>
																<Table
																	columnKey={modules.UNCLAIMED_GIFTS}
																	columns={columns}
																	tableData={tableData}
																	payload={payload}
																	sortCol={(obj) => {
																		setPayload({
																			...payload,
																			sort: {
																				[obj.alias]:
																					!obj.sort && obj.sort === ""
																						? undefined
																						: obj.sort,
																			},
																		});
																	}}
																	loading={isLoading}
																	filterValue={async (obj) => {
																		if (obj.filter === null) {
																			const resp = await CS.filterData(
																				payload.filter,
																				obj
																			);
																			setPayload({
																				...payload,
																				filter: resp,
																			});
																		} else {
																			setPayload({
																				...payload,
																				filter: {
																					...payload.filter,
																					[obj.alias]: obj.filter,
																				},
																			});
																		}
																	}}
																	filterDate={(dates) => {
																		setPayload({
																			...payload,
																			filter_date: dates && {
																				...payload?.filter_date,
																				[dates.alias]: dates.filter.map(
																					(value) => {
																						return new Date(
																							value
																						).toISOString();
																					}
																				),
																			},
																		});
																	}}
																/>
															</div>
														</div>
													</div>
												</div>
											) : isLoading ? (
												<div className="d-flex justify-content-center">
													<TableLoader />
												</div>
											) : null}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default UnClaimedGiftsPayout;
